'use client';

import * as React from 'react';
import { ResponsiveContainer, Tooltip } from 'recharts';
import { cn } from '../../lib/utils';

// ---------------------------------------------------------------------------
// Chart config (shadcn-style: maps data keys to label/color for tooltips/legend)
// ---------------------------------------------------------------------------

export type ChartConfig = Record<
  string,
  {
    label?: string;
    color?: string;
  }
>;

const ChartContext = React.createContext<{ config: ChartConfig }>({
  config: {}
});

function useChart() {
  const context = React.useContext(ChartContext);
  if (!context) {
    throw new Error('useChart must be used within a <ChartContainer />');
  }
  return context;
}

// ---------------------------------------------------------------------------
// ChartContainer - wraps Recharts with config context and responsive sizing
// ---------------------------------------------------------------------------

interface ChartContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  config: ChartConfig;
  children: React.ReactElement;
}

const ChartContainer = React.forwardRef<HTMLDivElement, ChartContainerProps>(
  ({ config, children, className, ...props }, ref) => {
    const contextValue = React.useMemo(() => ({ config }), [config]);
    return (
      <ChartContext.Provider value={contextValue}>
        <div
          ref={ref}
          className={cn('w-full', className)}
          style={{ minHeight: '200px' }}
          {...props}
        >
          <ResponsiveContainer width="100%" height="100%" minHeight={200}>
            {children}
          </ResponsiveContainer>
        </div>
      </ChartContext.Provider>
    );
  }
);
ChartContainer.displayName = 'ChartContainer';

export { ChartContainer, useChart, Tooltip as ChartTooltip };
